/**
 * Die Karte.
 *
 * Jedes Feld ist ein Button mit `aria-pressed` und einem zugaenglichen
 * Namen, der den **Zustand** mitnennt (NFR-A11Y-01): „Agentless,
 * markiert, B2, 4 Quellen". Wer die Karte hört statt sieht, braucht die
 * Bewertung genauso wie alle anderen.
 *
 * **Ein Tipp tut beides**: er markiert das Feld und oeffnet, was dahinter
 * steht. Das ist der ganze Trick an dieser Oberflaeche.
 *
 * Vorher gab es einen Schalter „Klartext zeigen", der die ganze Karte
 * umstellte. Das verlangt eine Entscheidung, bevor man weiss, worueber man
 * entscheidet: Wer eine Floskel hoert, will sie markieren **und** wissen,
 * was sie heisst, und nicht vorher einen Modus waehlen. Markieren bleibt
 * ein Tipp, das Nachlesen kommt gratis dazu.
 *
 * Wer das nicht will, schaltet es im Blatt selbst ab; dann markiert ein
 * Tipp nur noch, und der lange Druck bleibt der Weg zum Text.
 */
import { useCallback, useRef } from 'react'

import { useLang } from '../lang'
import type { CellStatus, Term } from '../lib/types'

const HOLD_MS = 420
const MOVE_TOLERANCE = 12

interface Props {
  cells: string[]
  terms: Record<string, Term>
  status: Record<number, CellStatus>
  winning: Set<number>
  flash: number[]
  disabled?: boolean
  onToggle: (cell: number) => void
  onInspect: (termId: string) => void
}

const GRADE_LABEL: Record<string, string> = {
  A1: 'A1', B2: 'B2', C3: 'C3', pend: '…', F6: 'F6', E5: 'E5',
}

export function Card({
  cells, terms, status, winning, flash, disabled, onToggle, onInspect,
}: Props) {
  const { t, pickText } = useLang()
  const hold = useRef<{ timer: number; x: number; y: number; fired: boolean } | null>(null)

  const start = useCallback(
    (termId: string, e: React.PointerEvent) => {
      const timer = window.setTimeout(() => {
        if (hold.current) hold.current.fired = true
        onInspect(termId)
      }, HOLD_MS)
      hold.current = { timer, x: e.clientX, y: e.clientY, fired: false }
    },
    [onInspect],
  )

  const move = useCallback((e: React.PointerEvent) => {
    const h = hold.current
    if (!h) return
    if (Math.abs(e.clientX - h.x) > MOVE_TOLERANCE || Math.abs(e.clientY - h.y) > MOVE_TOLERANCE) {
      window.clearTimeout(h.timer)
      hold.current = null
    }
  }, [])

  const end = useCallback(
    (cell: number) => {
      const h = hold.current
      hold.current = null
      if (!h) return
      window.clearTimeout(h.timer)
      if (h.fired) return
      // Nach einem langen Druck darf der Loslass-Tipp nicht auch noch
      // markieren, sonst setzt jedes Nachschlagen ein Feld.
      if (!disabled) onToggle(cell)
    },
    [disabled, onToggle],
  )

  return (
    <>
      {/* Fuenf Buchstaben ueber den Spalten. Rein dekorativ, darum
          `aria-hidden`: ein Screenreader soll die Karte vorlesen und nicht
          das Wort buchstabieren. */}
      <div className="bingohead" aria-hidden="true">
        {['B', 'I', 'N', 'G', 'O'].map((b) => (
          <span key={b}>{b}</span>
        ))}
      </div>
      <div className="grid" role="group" aria-label={t('app.name')}>
      {cells.map((termId, cell) => {
        const term = terms[termId]
        const state = status[cell]
        const isFree = cell === 12
        const label = isFree ? pickText(term?.text) || 'KI-gestützt' : pickText(term?.text) || termId
        const grade = isFree ? 'B2' : state?.grade
        const sources = state?.sources ?? 0

        const spoken = [
          label,
          isFree ? t('legend.b2') : state ? t('legend.title') : '',
          grade && !isFree ? grade : '',
          sources ? `${sources}` : '',
        ]
          .filter(Boolean)
          .join(', ')

        return (
          <button
            key={`${cell}-${termId}`}
            type="button"
            className={[
              'cell',
              isFree ? 'cell--free' : '',
              winning.has(cell) ? 'cell--won' : '',
              flash.includes(cell) ? 'cell--flash' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-grade={grade ?? undefined}
            data-cell={cell}
            aria-pressed={isFree ? true : Boolean(state)}
            aria-label={spoken}
            disabled={isFree}
            onPointerDown={(e) => !isFree && start(termId, e)}
            onPointerMove={move}
            onPointerUp={() => !isFree && end(cell)}
            onPointerCancel={() => {
              if (hold.current) window.clearTimeout(hold.current.timer)
              hold.current = null
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span>{label}</span>
            {!isFree && state ? (
              <span className="cell__badge" aria-hidden="true">
                {GRADE_LABEL[state.grade] ?? ''}
                {sources > 0 ? `·${sources}` : ''}
              </span>
            ) : null}
          </button>
        )
      })}
      </div>
    </>
  )
}

export function Legend() {
  const { t } = useLang()
  const rows: Array<[string, string]> = [
    ['A1', t('legend.a1')],
    ['B2', t('legend.b2')],
    ['C3', t('legend.c3')],
    ['…', t('legend.pend')],
    ['F6', t('legend.f6')],
    ['E5', t('legend.e5')],
  ]
  return (
    <div className="legend" aria-label={t('legend.title')}>
      {rows.map(([code, text]) => (
        <span key={code}>
          <b>{code}</b> {text}
        </span>
      ))}
    </div>
  )
}
